import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../data/constants";
import { BrandHexLogo } from "./BrandWordmark";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.id);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 border-b border-white/10 transition-colors duration-300 ${
        scrolled ? "bg-[#0B0F19]/90 backdrop-blur-xl" : "bg-[#0B0F19]/60 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 md:h-24 flex items-center justify-between gap-6">
        {/* Brand */}
        <a
          href="#home"
          onClick={scrollTo("home")}
          data-testid="header-brand"
          className="flex items-center gap-4 group"
        >
          <BrandHexLogo size={52} />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-condensed text-white text-3xl tracking-wide leading-[0.85]">
              SHOOLIN
            </span>
            <span className="font-condensed text-[#F47B20] text-[15px] tracking-[0.22em] leading-[1] mt-1">
              DIGITAL SERVICES
            </span>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((l) => {
            const isActive = active === l.id;
            return (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={scrollTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                className={`relative px-4 py-2 text-sm font-medium uppercase tracking-[0.18em] transition-colors duration-200 ${
                  isActive ? "text-[#F47B20]" : "text-white/70 hover:text-white"
                }`}
              >
                {l.label}
                <span
                  className={`absolute left-4 right-4 -bottom-0.5 h-[2px] origin-left bg-[#F47B20] transition-transform duration-300 ${
                    isActive ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="/industrial-authority"
            data-testid="header-offer-cta"
            className="inline-flex items-center gap-2 px-3 py-2 border border-[#F47B20]/50 text-[#F47B20] text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-[#F47B20] hover:text-black transition-colors"
          >
            ₹10,000 Offer
          </a>
          <a
            href={`tel:${COMPANY.phone}`}
            data-testid="header-phone-cta"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/80 hover:text-[#F47B20] transition-colors"
          >
            <Phone className="w-4 h-4" strokeWidth={2.4} />
            {COMPANY.phoneDisplay}
          </a>
          <a
            href="#contact"
            onClick={scrollTo("contact")}
            data-testid="header-contact-cta"
            className="inline-flex items-center px-5 py-3 bg-[#F47B20] text-black font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#FF8A33] hover:-translate-y-0.5 transition-all duration-300"
          >
            Get a Quote
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          data-testid="header-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-white/15 text-white"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div
          data-testid="header-mobile-menu"
          className="md:hidden border-t border-white/10 bg-[#0B0F19]/95 backdrop-blur-xl"
        >
          <nav className="px-6 py-4 flex flex-col">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={scrollTo(l.id)}
                data-testid={`nav-mobile-${l.id}`}
                className={`py-3 border-b border-white/5 text-sm font-bold uppercase tracking-[0.2em] ${
                  active === l.id ? "text-[#F47B20]" : "text-white"
                }`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid="header-mobile-phone"
              className="py-3 border-b border-white/5 text-sm font-bold uppercase tracking-[0.2em] text-white/80"
            >
              Call {COMPANY.phoneDisplay}
            </a>
            <a
              href="#contact"
              onClick={scrollTo("contact")}
              data-testid="header-mobile-quote"
              className="mt-3 inline-flex items-center justify-center px-5 py-3 bg-[#F47B20] text-black font-bold uppercase tracking-[0.18em] text-xs"
            >
              Get a Quote
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
